import React, { useEffect, useState } from "react";
import { freight } from "../../lib/freightApi.js";
import { useCarrier } from "./CarrierApp.jsx";
import { Shell, Back, Title, Card, Button, Field, Select, Note, Quiet, NotRunning, C, errText } from "./ui.jsx";

/**
 * People in the loop (the schematic's Settings · People in the loop board): who Alfred hands things
 * to, in order, and how each person is reached. Admins see and change everyone; anyone else sees and
 * changes only how they themselves are reached (backend loop.mjs).
 *
 * ⛔ WHAT RUNS: Hiring's first person is the recruiter, and once the carrier is live Alfred really
 * puts applicants through to them and emails each hand-off. The dispatch list, and anyone after the
 * recruiter, is stored for the escalation down the list, which is not built: marked "Not running yet".
 * Texts start only after the person opts in on their own phone.
 */

const visuallyHidden = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" };

function Check({ id, label, note, checked, disabled, onChange }) {
  return (
    <label htmlFor={id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 16, minHeight: 36, color: disabled ? C.muted : C.ink }}>
      <input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} style={{ width: 20, height: 20 }} />
      <span>{label}{note ? <span style={{ color: C.muted }}>{` · ${note}`}</span> : null}</span>
    </label>
  );
}

export function PersonForm({ p, onSave, onCancel, busy, withList }) {
  const [f, setF] = useState({ list: p?.list || "dispatch", name: p?.name || "", phone: p?.phone || "", email: p?.email || "", hours: p?.hours || "", gets: p?.gets || "" });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const idp = `loop-${p?.id ?? "new"}`;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
        {withList && <Select id={`${idp}-list`} label="List" value={f.list} onChange={set("list")} options={[{ v: "dispatch", label: "Dispatch" }, { v: "hiring", label: "Hiring" }]} />}
        <Field id={`${idp}-name`} label="Name" value={f.name} onChange={set("name")} />
        <Field id={`${idp}-phone`} label="Cell" type="tel" value={f.phone} onChange={set("phone")} />
        <Field id={`${idp}-email`} label="Email" type="email" value={f.email} onChange={set("email")} />
        <Field id={`${idp}-hours`} label="Hours" value={f.hours} onChange={set("hours")} placeholder="Weekdays, 7 AM to 6 PM" />
        <Field id={`${idp}-gets`} label="Gets" value={f.gets} onChange={set("gets")} placeholder="Late loads, missed check calls" />
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <Button disabled={busy || !f.name.trim()} onClick={() => onSave(f)}>{p ? "Save" : "Add the person"}</Button>
        <Button kind="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export function LoopRow({ p, n, admin, busy, editing, setEditing, act, first, last, tag }) {
  const idp = `lp-${p.id}`;
  const [confirming, setConfirming] = useState(false);
  return (
    <div style={{ padding: "18px 24px", display: "flex", flexDirection: "column", gap: 10, borderBottom: last ? "none" : `1px solid ${C.soft}` }}>
      {editing === p.id ? (
        <PersonForm p={p} busy={busy} onCancel={() => setEditing(null)} onSave={(f) => act(() => freight.updateLoopPerson(p.id, { name: f.name, phone: f.phone, email: f.email, hours: f.hours, gets: f.gets }), "Saved.")} />
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "8px 16px", flexWrap: "wrap" }}>
            <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              <span style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 17, fontWeight: 600 }}>{`${n}. ${p.name}${p.mine ? " (you)" : ""}`}</span>
                {tag && <NotRunning />}
              </span>
              {[p.hours, p.phone, p.email].some(Boolean) && <span style={{ fontSize: 15, color: C.muted }}>{[p.hours, p.phone, p.email].filter(Boolean).join(" · ")}</span>}
              {p.gets && <span style={{ fontSize: 15, color: C.muted }}>{`Gets: ${p.gets}`}</span>}
            </span>
            {admin && (
              <span style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "flex-start" }}>
                <Button kind="quiet" disabled={busy || first} onClick={() => act(() => freight.moveLoopPerson(p.id, "up"))} label={`Move ${p.name} up`} style={{ padding: "0 8px" }}>Up</Button>
                <Button kind="quiet" disabled={busy || last} onClick={() => act(() => freight.moveLoopPerson(p.id, "down"))} label={`Move ${p.name} down`} style={{ padding: "0 8px" }}>Down</Button>
                <Button kind="quiet" onClick={() => setEditing(p.id)} label={`Edit ${p.name}`} style={{ padding: "0 8px" }}>Edit</Button>
                {confirming ? (
                  <>
                    <Button kind="quiet" disabled={busy} onClick={() => act(() => freight.removeLoopPerson(p.id), `${p.name} is off the list.`)} style={{ padding: "0 8px" }}>{`Yes, remove ${p.name}`}</Button>
                    <Button kind="quiet" disabled={busy} onClick={() => setConfirming(false)} label={`Keep ${p.name}`} style={{ padding: "0 8px" }}>Keep</Button>
                  </>
                ) : <Button kind="quiet" disabled={busy} onClick={() => setConfirming(true)} label={`Remove ${p.name}`} style={{ padding: "0 8px" }}>Remove</Button>}
              </span>
            )}
          </div>
          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend style={visuallyHidden}>{`How ${p.name} gets it`}</legend>
            <div style={{ display: "flex", gap: "4px 20px", flexWrap: "wrap", alignItems: "center" }}>
              <Check id={`${idp}-t`} label="Warm transfer" checked={p.byTransfer} disabled={busy || !p.phone} note={p.phone ? null : "needs a cell"}
                onChange={(v) => act(() => freight.updateLoopPerson(p.id, { byTransfer: v }))} />
              <Check id={`${idp}-e`} label="Email" checked={p.byEmail} disabled={busy || !p.email} note={p.email ? null : "needs an address"}
                onChange={(v) => act(() => freight.updateLoopPerson(p.id, { byEmail: v }))} />
              <Check id={`${idp}-x`} label="Text" checked={p.byText} disabled={busy || !p.textOptedIn}
                note={p.textOptedIn ? "opted in · texting is not connected yet" : "not opted in yet"} onChange={(v) => act(() => freight.updateLoopPerson(p.id, { byText: v }))} />
              {admin && !p.textOptedIn && p.email && (
                <Button kind="quiet" disabled={busy} onClick={() => act(() => freight.sendOptIn(p.id), null, (r) => (r?.sent ? `The setup link went to ${p.email}.` : `The setup link did not go: ${r?.error || "unknown"}.`))}
                  style={{ padding: 0, minHeight: 32 }}>Send setup link</Button>
              )}
            </div>
          </fieldset>
        </>
      )}
    </div>
  );
}

export function RecruiterRow({ r, live, admin, editing, setEditing, busy, act }) {
  const transfer = r.transferReady ? "ready" : live ? (r.phone ? "not set up" : "needs a cell") : "set up at go-live";
  return (
    <div style={{ padding: "18px 24px", display: "flex", flexDirection: "column", gap: 10, borderBottom: `1px solid ${C.soft}` }}>
      {editing === "recruiter" ? (
        <RecruiterForm r={r} busy={busy} onCancel={() => setEditing(null)}
          onSave={(f) => act(() => freight.saveRecruiting({ recruiter: f }), "The recruiter is saved.")} />
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "8px 16px", flexWrap: "wrap" }}>
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 17, fontWeight: 600 }}>{`1. ${r.name || r.email || "The recruiter"}${r.mine ? " (you)" : ""}`}</span>
              {[r.phone, r.email].some(Boolean) && <span style={{ fontSize: 15, color: C.muted }}>{[r.phone, r.email].filter(Boolean).join(" · ")}</span>}
              <span style={{ fontSize: 15, color: C.muted }}>Gets: applicants ready for a recruiter</span>
            </span>
            {admin && <Button kind="quiet" onClick={() => setEditing("recruiter")} style={{ padding: "0 8px" }}>Edit</Button>}
          </div>
          <div style={{ display: "flex", gap: "4px 20px", flexWrap: "wrap" }}>
            <Check id="rec-t" label="Warm transfer" checked={r.transferReady} disabled note={transfer} onChange={() => {}} />
            <Check id="rec-e" label="Email" checked={!!r.email} disabled note={r.email ? "each hand-off" : "needs an address"} onChange={() => {}} />
            <Check id="rec-x" label="Text" checked={false} disabled note="not connected yet" onChange={() => {}} />
          </div>
          {r.transferError && <span style={{ fontSize: 14, color: C.bad }}>{`The transfer could not be set up: ${r.transferError}`}</span>}
        </>
      )}
    </div>
  );
}

function RecruiterForm({ r, onSave, onCancel, busy }) {
  const [f, setF] = useState({ name: r?.name || "", phone: r?.phone || "", email: r?.email || "" });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
        <Field id="rec-name" label="Recruiter's name" value={f.name} onChange={set("name")} />
        <Field id="rec-phone" label="Cell (for warm transfers)" type="tel" value={f.phone} onChange={set("phone")} />
        <Field id="rec-email" label="Email (for each hand-off)" type="email" value={f.email} onChange={set("email")} />
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <Button disabled={busy} onClick={() => onSave(f)}>Save</Button>
        <Button kind="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

/** The opt-in link to share, copied for real (or said so), and replaceable if it went too far. */
export function LinkCard({ link, onNewLink, busy }) {
  const [copied, setCopied] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied("Copied."); }
    catch { setCopied("Could not copy. Select the link and copy it by hand."); }
  };
  return (
    <Card style={{ padding: "16px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", gap: "6px 16px", flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 16, fontWeight: 600 }}>Setup link</span>
        <span style={{ fontSize: 15, color: C.muted, flex: "1 1 260px", overflowWrap: "anywhere" }}>{link || "Our server is missing its web address. Ask MuleWorks."}</span>
        {link && <Button kind="quiet" onClick={copy} label="Copy the setup link" style={{ padding: 0, minHeight: 32 }}>Copy</Button>}
        {link && (confirming
          ? <Button kind="quiet" disabled={busy} onClick={async () => { setConfirming(false); setCopied(null); await onNewLink(); }} style={{ padding: 0, minHeight: 32 }}>Yes, make a new link</Button>
          : <Button kind="quiet" disabled={busy} onClick={() => setConfirming(true)} style={{ padding: 0, minHeight: 32 }}>New link</Button>)}
      </div>
      {confirming && <Quiet>The old link stops working at once. Anyone who has not answered yet needs the new one.</Quiet>}
      {copied && <span role="status" style={{ fontSize: 14, color: C.muted }}>{copied}</span>}
    </Card>
  );
}

export function PeopleView({ company, stage, me, board, error, busy, msg, editing, setEditing, act, link, onNewLink }) {
  const admin = !!me?.admin;
  // Dispatch is marked as a whole; in Hiring only the people after the recruiter are not running.
  const section = (id, title, list, extra, { wholeTag, rowTag }) => (
    <section aria-labelledby={id} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <span style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <h2 id={id} style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{title}</h2>
        {wholeTag && <NotRunning />}
      </span>
      <Card style={{ display: "flex", flexDirection: "column" }}>
        {extra}
        {list.map((p, i) => (
          <LoopRow key={p.id} p={p} n={i + 1 + (extra ? 1 : 0)} admin={admin} busy={busy} editing={editing} setEditing={setEditing} act={act}
            first={i === 0} last={i === list.length - 1} tag={rowTag} />
        ))}
        {!extra && list.length === 0 && <div style={{ padding: "18px 24px" }}><Quiet>Nobody on this list yet.</Quiet></div>}
      </Card>
    </section>
  );
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier/settings">Settings</Back>
      <Title sub={admin ? "Who Alfred hands things to, in order, and how each person is reached." : "How Alfred reaches you."}>People in the loop</Title>
      {error && <Quiet>{error}</Quiet>}
      {!board && !error && <Quiet>Loading…</Quiet>}
      {board && (
        <>
          {admin && (editing === "add"
            ? <Card style={{ padding: 24 }}><PersonForm withList busy={busy} onCancel={() => setEditing(null)} onSave={(f) => act(() => freight.addLoopPerson(f), `${f.name.trim()} is on the list.`)} /></Card>
            : <div><Button onClick={() => setEditing("add")} style={{ minHeight: 48, fontSize: 16, padding: "0 22px" }}>Add a person</Button></div>)}
          <Note msg={msg} />
          {admin && link !== undefined && <LinkCard link={link} onNewLink={onNewLink} busy={busy} />}
          {(admin || board.dispatch.length > 0) && section("dispatch-title", "Dispatch, in order", board.dispatch, null, { wholeTag: true })}
          {(admin || board.hiring.length > 0 || board.recruiter) && section("hiring-title", "Hiring, in order", board.hiring,
            board.recruiter || admin ? <RecruiterRow r={board.recruiter || {}} live={board.live} admin={admin} editing={editing} setEditing={setEditing} busy={busy} act={act} /> : null,
            { rowTag: true })}
          {!admin && !board.dispatch.length && !board.hiring.length && !board.recruiter && <Quiet>You are not on any list yet.</Quiet>}
        </>
      )}
    </Shell>
  );
}

export default function People() {
  const { carrier, me } = useCarrier();
  const [board, setBoard] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [editing, setEditing] = useState(null);
  const reload = () => freight.loop().then(setBoard).catch((e) => setError(errText(e)));
  useEffect(() => { reload(); }, []);
  const [link, setLink] = useState(undefined);
  useEffect(() => { if (me.admin) freight.optInLink().then(setLink).catch(() => setLink(null)); }, [me.admin]);
  const act = async (fn, ok, words) => {
    setBusy(true); setMsg(null);
    try {
      const r = await fn();
      if (r?.dispatch) setBoard(r); else await reload();
      if (ok || words) setMsg({ ok: true, text: words ? words(r) : ok });
      setEditing(null);
    } catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  const onNewLink = () => act(async () => { setLink(await freight.newOptInLink()); }, "A new setup link is ready. The old one no longer works.");
  return <PeopleView company={carrier.name} stage={carrier.stage} me={me} board={board} error={error} busy={busy} msg={msg} editing={editing} setEditing={setEditing} act={act}
    link={link} onNewLink={onNewLink} />;
}
